import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const PayIncentives = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Pay & Incentives" 
        description="Complete compensation structure for BlueLux technicians"
      >
        <section className="space-y-8">
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Base Pay</h2>
            <div className="space-y-3">
              <p className="text-lg">
                <strong>$20</strong> gross per pool serviced
              </p>
              <p className="text-muted-foreground">
                Minus <strong>$5 service fee deduction</strong> per pool (covers chemicals, equipment, and platform support)
              </p>
              <p className="text-lg font-medium">
                = <strong>$15 net per pool</strong> paid to you
              </p>
              <div className="bg-card border border-border rounded-lg p-4 mt-4">
                <p className="text-sm font-medium mb-2">Rule: No app submission = no pay</p>
                <p className="text-sm text-muted-foreground">
                  Base pay verified through the BlueLux Technician App:
                </p>
                <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
                  <li>Service checklist completed</li>
                  <li>Water chemistry readings logged</li>
                  <li>Chemical dosing logged</li>
                  <li>Photos uploaded (before/after)</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
          <h2 className="text-2xl font-semibold text-foreground mb-4">Mobility Stipend</h2>
            <p className="text-muted-foreground">
              If you use your own vehicle: <strong>$1.20 per pool</strong> mobility stipend to offset fuel costs
            </p>
            <p className="text-muted-foreground mt-2">
              If you use a BlueLux-provided vehicle: <strong>$15 per day</strong> lease fee will be deducted instead
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Quality & Retention Bonuses</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-medium">Quality & Professionalism Bonus</h3>
                <p className="text-xl font-semibold text-primary">$100 per month</p>
                <p className="text-sm text-muted-foreground mt-2">Requirements (ALL must be met):</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground mt-1 space-y-1">
                  <li>100% app compliance (checklist, readings, photos)</li>
                  <li>Professional appearance (uniform, neat presentation)</li>
                  <li>Zero customer complaints logged</li>
                </ul>
              </div>

              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-medium">Customer Retention Bonus</h3>
                <p className="text-xl font-semibold text-primary">$50 per customer</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Earned when a customer remains active for 12 consecutive months
                </p>
              </div>

              <div className="border-l-4 border-primary pl-4">
                <h3 className="text-lg font-medium">Safety & Reliability Bonus</h3>
                <p className="text-xl font-semibold text-primary">$250 per quarter</p>
                <p className="text-sm text-muted-foreground mt-2">Requirements:</p>
                <ul className="list-disc list-inside text-sm text-muted-foreground mt-1 space-y-1">
                  <li>Zero missed visits</li>
                  <li>Zero accidents</li>
                  <li>On-time reporting for all pools</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Specialty Service Pay</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-accent pl-4">
                <h3 className="text-lg font-medium">Filter Clean</h3>
                <p className="text-xl font-semibold text-accent">$40</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Per completed filter clean service
                </p>
              </div>

              <div className="border-l-4 border-accent pl-4">
                <h3 className="text-lg font-medium">Salt Cell Clean</h3>
                <p className="text-xl font-semibold text-accent">$10</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Per completed salt cell clean service
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Growth Incentives</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-accent pl-4">
                <h3 className="text-lg font-medium">Customer Referral Bonus</h3>
                <p className="text-xl font-semibold text-accent">$100</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Paid after referred customer remains active for 90 days
                </p>
              </div>

              <div className="border-l-4 border-accent pl-4">
                <h3 className="text-lg font-medium">Technician Referral Bonus</h3>
                <p className="text-xl font-semibold text-accent">$100</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Paid after referred technician remains employed for 90 days
                </p>
              </div>

              <div className="border-l-4 border-accent pl-4">
                <h3 className="text-lg font-medium">Chemical Upsell Commission</h3>
                <p className="text-xl font-semibold text-accent">15%</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Commission on chemical upsells (conditioner, phosphate remover, specialty treatments)
                </p>
                <p className="text-sm text-muted-foreground">
                  Routine service chemicals are excluded. Paid monthly.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Loyalty & Career Path</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-3">Tenure Bonuses</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li><strong>6 months</strong> of continuous service: $250</li>
                  <li><strong>12 months</strong> of continuous service: $250</li>
                  <li><strong>24 months</strong> of continuous service: $500</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Tiered Pay System</h3>
                <p className="text-muted-foreground mb-2">Per-pool rate increases based on performance:</p>
                <ul className="space-y-2 text-muted-foreground">
                  <li><strong>Year 1:</strong> $15 net per pool</li>
                  <li><strong>Year 2:</strong> $16 net per pool (if retention + quality targets met)</li>
                  <li><strong>Year 3:</strong> $17 net per pool (if retention + quality targets met)</li>
                  <li><strong>Top performers:</strong> Eligible to earn up to $18 net per pool</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Route Coverage Bonus</h3>
                <p className="text-muted-foreground">
                  Covering another technician's route (vacation/sick leave): <strong>$5 extra per pool</strong> for those jobs
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Route Ownership Path</h3>
                <p className="text-muted-foreground">
                  After <strong>3+ years</strong> of service, top-performing technicians may be eligible for:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li><strong>Route Buy-In:</strong> Discounted transfer pricing for their own route</li>
                  <li><strong>Profit Sharing:</strong> Percentage of revenue generated from their managed route</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Payment Schedule</h2>
            <p className="text-muted-foreground mb-3">
              Payments are processed <strong>twice monthly</strong> via direct deposit for completed and approved jobs.
            </p>
            <p className="text-muted-foreground">
              If a payment date is not expressly specified for any service, BlueLux will remit payment no later than 
              thirty (30) days after receipt of a valid invoice, as required by California law.
            </p>
            <p className="text-muted-foreground mt-3">
              You will receive an annual <strong>Form 1099-NEC</strong> for all compensation paid.
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-medium mb-3">Estimated Monthly Earnings Example</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Number of pools serviced × Net rate per pool ($15) = Base monthly earnings
            </p>
            <div className="space-y-2">
              <p className="text-sm">
                <strong>Example:</strong> 100 pools per month at $15/pool = <strong>$1,500 base</strong>
              </p>
              <p className="text-sm text-muted-foreground">
                Plus bonuses, incentives, and tiered pay increases as you grow with BlueLux
              </p>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default PayIncentives;
